import React, { useEffect, useCallback, useRef, useState, useMemo } from 'react';
import { Drawer } from 'vaul';
import { Modal } from './Modal';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useFeatures } from '../../contexts/FeaturesContext';
import { ShareSheet, type ShareEntityType } from '../sharing/ShareSheet';
import { ShareIcon } from '../Icons';

export interface ShareConfig {
  entityType: ShareEntityType;
  entityId: string | number;
  entityTitle: string;
  shareCount?: number;
  onShareSuccess?: () => void;
}

export type SheetSnap = 'peek' | 'half' | 'full';

export interface SheetProps {
  /** Canonical open prop (replaces deprecated isOpen) */
  open?: boolean;
  /** Canonical dismiss callback (replaces deprecated onClose) */
  onDismiss?: () => void;
  /** @deprecated Use open */
  isOpen?: boolean;
  /** @deprecated Use onDismiss */
  onClose?: () => void;
  title?: string;
  footer?: React.ReactNode;
  children: React.ReactNode;
  shareConfig?: ShareConfig;
  /** When false, hides X button and disables backdrop/swipe/Escape dismiss. Default: true */
  dismissible?: boolean;
  /**
   * When true, suppresses the explicit X close button even if `dismissible`
   * is true. Independent of `dismissible` — use when the caller renders its
   * own explicit exits and wants no default close affordance at all, on
   * both the mobile touch-sheet AND the desktop Modal delegate (see
   * SheetDesktop below, which forwards both props to Modal). Default false.
   */
  hideCloseButton?: boolean;
  /** Accessible name when there is no visible `title` (defaults to title, then 'Sheet'). */
  ariaLabel?: string;
  /** When true, shows "Discard changes?" confirmation before dismissing */
  dirtyGuard?: boolean;

  // ─── V2 snap-point props (spec 192) ────────────────────────────────
  // Ignored when sheet_v2_snappable flag is off.
  /** Snap-point set. Defaults to ['peek','half','full']. */
  snapPoints?: SheetSnap[];
  /**
   * Snap point the sheet opens at. Defaults to 'full' so the V2 path matches
   * V1's "content fills available space" ergonomics when callers haven't
   * opted into progressive disclosure. Callers that want peek/half behavior
   * must set this explicitly — and when they do, `autoSnapOnFocus` takes over
   * the moment the user engages a form field.
   */
  initialSnap?: SheetSnap;
  /** Auto-snap to 'full' when a descendant input gains focus. Default true. */
  autoSnapOnFocus?: boolean;
  /**
   * Width threshold (px) below which Sheet renders its touch-first mobile
   * variant instead of delegating to the desktop Modal. Passed straight
   * through to useIsMobile(). Default: useIsMobile()'s own default (768) —
   * most callers don't need to set this. Spec 285's message-actions sheet
   * passes 1024 so it renders as a touch sheet at tablet widths too, not
   * just below 768px (useIsMobile()'s 768 default would otherwise render
   * the desktop Modal at 768-1023px, defeating the touch-first requirement).
   */
  mobileBreakpoint?: number;
}

export function Sheet(props: SheetProps) {
  const { features } = useFeatures();
  const isMobile = useIsMobile(props.mobileBreakpoint);

  if (!isMobile) return <SheetDesktop {...props} />;
  if (features.sheetV2Snappable) return <SheetMobileV2 {...props} />;
  return <SheetMobileV1 {...props} />;
}

// ════════════════════════════════════════════════════════════════════
// Desktop — delegates to Modal (unchanged from pre-spec-192 behavior)
// ════════════════════════════════════════════════════════════════════

function SheetDesktop(props: SheetProps) {
  const {
    open, onDismiss, isOpen, onClose, title, footer, children, shareConfig,
    dismissible = true, hideCloseButton = false, ariaLabel,
  } = props;
  const resolvedOpen = open ?? isOpen ?? false;
  const noopDismiss = useMemo<() => void>(() => () => {}, []);
  const resolvedDismiss = onDismiss ?? onClose ?? noopDismiss;

  return (
    <Modal
      isOpen={resolvedOpen}
      onClose={resolvedDismiss}
      title={title}
      size="md"
      footer={footer}
      shareConfig={shareConfig}
      dismissible={dismissible}
      hideCloseButton={hideCloseButton}
      ariaLabel={ariaLabel}
    >
      {children}
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════
// V1 Mobile — preserved current behavior for sheet_v2_snappable=false
// ════════════════════════════════════════════════════════════════════

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// In Capacitor WKWebView (contentInset:"never"), dvh===lvh always (keyboard doesn't
// resize the viewport) and position:fixed on body breaks native scroll restoration.
// Detect once at module load so the guards below are free of React effects.
const IS_CAPACITOR =
  typeof window !== 'undefined' &&
  !!(window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } })
    .Capacitor?.isNativePlatform?.();

function SheetMobileV1(props: SheetProps) {
  const {
    open,
    onDismiss,
    isOpen,
    onClose,
    title,
    footer,
    children,
    shareConfig,
    dismissible = true,
    hideCloseButton = false, ariaLabel,
    dirtyGuard = false,
  } = props;

  const resolvedOpen = open ?? isOpen ?? false;
  const noopDismiss = useMemo<() => void>(() => () => {}, []);
  const resolvedDismiss = onDismiss ?? onClose ?? noopDismiss;
  // Use localStorage so the debug flag survives auth redirects (URL params get stripped on login).
  // Enable via browser console: localStorage.setItem('helmDebugVV','true')
  const showDebug = process.env.NODE_ENV !== 'production' && typeof window !== 'undefined' && localStorage.getItem('helmDebugVV') === 'true';

  const sheetRef = useRef<HTMLDivElement>(null);
  const [sharingOpen, setSharingOpen] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const triggerRef = useRef<Element | null>(null);

  const startY = useRef(0);
  const startTime = useRef(0);
  const currentY = useRef(0);
  const isDragging = useRef(false);

  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  const [entered, setEntered] = useState(false);

  const [debugVV, setDebugVV] = useState({ innerH: 0, vvH: 0, vvOff: 0, dvh: 0, lvh: 0, bodyPos: '', bodyScroll: 0, ios: '' });
  useEffect(() => {
    if (!showDebug || !resolvedOpen) return;
    const vv = window.visualViewport;
    const update = () => {
      const probe = document.createElement('div');
      probe.style.cssText = 'position:fixed;top:0;left:0;visibility:hidden;pointer-events:none;';
      document.body.appendChild(probe);
      probe.style.height = '100dvh';
      const dvhPx = probe.offsetHeight;
      probe.style.height = '100lvh';
      const lvhPx = probe.offsetHeight;
      probe.remove();
      setDebugVV({
        innerH: window.innerHeight,
        vvH: Math.round(vv?.height ?? -1),
        vvOff: Math.round(vv?.offsetTop ?? -1),
        dvh: dvhPx,
        lvh: lvhPx,
        bodyPos: document.body.style.position || 'static',
        bodyScroll: Math.round(document.documentElement.scrollTop),
        ios: (/CPU iPhone OS (\d+)/.exec(navigator.userAgent) ?? /OS (\d+)/.exec(navigator.userAgent) ?? ['', '?'])[1],
      });
    };
    update();
    const id = setInterval(update, 400);
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    return () => {
      clearInterval(id);
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
    };
  }, [resolvedOpen, showDebug]);

  useEffect(() => {
    if (resolvedOpen) {
      triggerRef.current = document.activeElement;
    } else {
      if (triggerRef.current && 'focus' in triggerRef.current) {
        (triggerRef.current as HTMLElement).focus();
      }
      triggerRef.current = null;
      setConfirmDiscard(false);
    }
  }, [resolvedOpen]);

  useEffect(() => {
    if (resolvedOpen) {
      if (sheetRef.current) {
        sheetRef.current.style.transform = 'translateY(100%)';
        sheetRef.current.style.transition = 'none';
      }
      const raf = requestAnimationFrame(() => {
        if (sheetRef.current) {
          const duration = prefersReducedMotion.current ? '0ms' : '320ms';
          sheetRef.current.style.transition = `transform ${duration} cubic-bezier(0.32, 0.72, 0, 1)`;
          sheetRef.current.style.transform = 'translateY(0)';
        }
        setEntered(true);
      });
      return () => {
        cancelAnimationFrame(raf);
        setEntered(false);
      };
    }
    setEntered(false);
  }, [resolvedOpen]);

  const animateOut = useCallback((cb: () => void) => {
    if (!sheetRef.current || prefersReducedMotion.current) {
      cb();
      return;
    }
    const duration = 240;
    const el = sheetRef.current;
    el.style.transition = `transform ${duration}ms ease-in`;
    el.style.transform = 'translateY(100%)';
    setTimeout(() => { if (el.isConnected) cb(); }, duration);
  }, []);

  const tryDismiss = useCallback(() => {
    if (!dismissible) return;
    if (dirtyGuard) {
      setConfirmDiscard(true);
      return;
    }
    animateOut(resolvedDismiss);
  }, [dismissible, dirtyGuard, animateOut, resolvedDismiss]);

  const handleFocusTrap = useCallback((e: KeyboardEvent) => {
    if (e.key !== 'Tab' || !sheetRef.current) return;
    const focusable = Array.from(sheetRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        handleFocusTrap(e);
        return;
      }
      if (e.key === 'Escape') tryDismiss();
    },
    [handleFocusTrap, tryDismiss]
  );

  useEffect(() => {
    if (!resolvedOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [resolvedOpen, handleKeyDown]);

  useEffect(() => {
    if (!resolvedOpen) return;
    // iOS Safari: body.overflow:hidden does NOT prevent focus-induced body scroll for
    // contenteditable (e.g. TipTap). body{position:fixed} removes body from the scroll
    // stack, forcing iOS to scroll the nearest overflow:auto ancestor instead (the
    // sheet's inner content div) — same behaviour as native <input>/<textarea>.
    // Capacitor WKWebView is excluded: position:fixed on body breaks native behaviour there.
    const scrollY = IS_CAPACITOR ? 0 : window.scrollY;
    const prev = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      height: document.body.style.height,
    };
    document.body.style.overflow = 'hidden';
    if (!IS_CAPACITOR) {
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      // body{position:fixed} creates a containing block for position:fixed descendants
      // (e.g. MobileBottomNav). Setting width+height=100% of viewport ensures those
      // elements still measure bottom:0 relative to the viewport edge, not body's
      // natural content height.
      document.body.style.width = '100%';
      document.body.style.height = '100vh';
    }
    return () => {
      document.body.style.overflow = prev.overflow;
      document.body.style.position = prev.position;
      document.body.style.top = prev.top;
      document.body.style.left = prev.left;
      document.body.style.right = prev.right;
      document.body.style.width = prev.width;
      document.body.style.height = prev.height;
      if (!IS_CAPACITOR) window.scrollTo(0, scrollY);
    };
  }, [resolvedOpen]);

  useEffect(() => {
    if (!resolvedOpen) return;
    const timer = setTimeout(() => {
      const focusable = sheetRef.current?.querySelector<HTMLElement>(FOCUSABLE);
      focusable?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, [resolvedOpen]);

  // iOS Safari keyboard avoidance for contenteditable (TipTap).
  //
  // When the keyboard appears and iOS scrolls the visual viewport to show the caret
  // (visualViewport.offsetTop > 0), position:fixed elements appear to shift upward
  // relative to what the user sees. A sheet at bottom:0 / maxHeight:90dvh can have
  // its header clipped above the visible screen.
  //
  // Fix: when the keyboard is up (layoutH - vvTop - vvH > threshold), pin the sheet
  // to the BOTTOM of the visual viewport and cap its height to 90% of the visible
  // area. Both `bottom` and `maxHeight` must be set — adjusting only one is not
  // sufficient to keep the header visible.
  //
  // Skipped for Capacitor: WKWebView with contentInset:never handles this natively
  // and body:fixed is not applied there.
  useEffect(() => {
    if (IS_CAPACITOR || !resolvedOpen) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const sheetEl = sheetRef.current;
    const update = () => {
      if (!sheetEl) return;
      const layoutH = window.innerHeight;
      const vvH = Math.round(vv.height);
      const vvTop = Math.round(vv.offsetTop);
      // Space below the visual viewport's bottom (occupied by keyboard).
      // Formula holds whether iOS shifts offsetTop or not.
      const belowVV = Math.max(0, layoutH - vvTop - vvH);
      if (belowVV > 50) {
        sheetEl.style.bottom = belowVV + 'px';
        sheetEl.style.maxHeight = Math.round(vvH * 0.9) + 'px';
      } else {
        sheetEl.style.bottom = '';
        sheetEl.style.maxHeight = '';
      }
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      if (sheetEl) {
        sheetEl.style.bottom = '';
        sheetEl.style.maxHeight = '';
      }
    };
  }, [resolvedOpen]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!dismissible) return;
    startY.current = e.clientY;
    startTime.current = Date.now();
    currentY.current = 0;
    isDragging.current = true;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [dismissible]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDragging.current) return;
    const deltaY = e.clientY - startY.current;
    if (deltaY < 0) return;
    currentY.current = deltaY;
    if (sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${deltaY}px)`;
      sheetRef.current.style.transition = 'none';
    }
  }, []);

  const handlePointerUp = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const deltaY = currentY.current;
    const elapsed = Date.now() - startTime.current;
    const velocity = elapsed > 0 ? deltaY / elapsed : 0;
    currentY.current = 0;

    if (velocity > 0.5 || deltaY > window.innerHeight * 0.3) {
      if (dirtyGuard) {
        if (sheetRef.current) {
          const dur = prefersReducedMotion.current ? '0ms' : '320ms';
          sheetRef.current.style.transition = `transform ${dur} cubic-bezier(0.32, 0.72, 0, 1)`;
          sheetRef.current.style.transform = 'translateY(0)';
        }
        setConfirmDiscard(true);
      } else {
        animateOut(resolvedDismiss);
      }
    } else {
      if (sheetRef.current) {
        const dur = prefersReducedMotion.current ? '0ms' : '320ms';
        sheetRef.current.style.transition = `transform ${dur} cubic-bezier(0.32, 0.72, 0, 1)`;
        sheetRef.current.style.transform = 'translateY(0)';
      }
    }
  }, [dirtyGuard, animateOut, resolvedDismiss]);

  if (!resolvedOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-[200]"
        onClick={dismissible ? tryDismiss : undefined}
        aria-hidden="true"
        style={{
          opacity: entered ? 1 : 0,
          transition: prefersReducedMotion.current ? 'none' : 'opacity 150ms',
        }}
      />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || title || 'Sheet'}
        className={[
          'fixed inset-x-0 bottom-0 z-[200]',
          'bg-surface rounded-t-2xl shadow-2xl',
          'flex flex-col',
        ].join(' ')}
        style={{ height: 'auto', minHeight: '40vh', maxHeight: '90dvh', transform: 'translateY(100%)' }}
      >
        {confirmDiscard && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-surface rounded-t-2xl px-6 gap-4">
            <p className="text-base font-semibold text-text-primary text-center">Discard changes?</p>
            <p className="text-sm text-text-secondary text-center">Your changes will be lost.</p>
            <div className="flex gap-3 w-full">
              <button
                type="button"
                className="flex-1 py-2.5 rounded-lg border border-border-light text-text-secondary hover:text-text-primary transition-colors text-sm font-medium"
                onClick={() => setConfirmDiscard(false)}
              >
                Keep editing
              </button>
              <button
                type="button"
                className="flex-1 py-2.5 rounded-lg bg-destructive text-white text-sm font-medium hover:opacity-90 transition-opacity"
                onClick={() => { setConfirmDiscard(false); resolvedDismiss(); }}
              >
                Discard
              </button>
            </div>
          </div>
        )}

        <div
          className="flex justify-center pt-3 pb-1 cursor-grab active:cursor-grabbing touch-none"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          aria-label="Drag to resize or swipe down to close"
        >
          <div className="w-9 h-1 rounded-full bg-border-light" />
        </div>

        <div className="flex items-center justify-between px-4 pt-2 pb-0 flex-shrink-0">
          <div className="flex items-center gap-2">
            {sharingOpen && (
              <button
                type="button"
                onClick={() => setSharingOpen(false)}
                className="min-w-touch min-h-touch flex items-center justify-center rounded-lg text-text-secondary hover:text-text-primary transition-colors -ml-2"
                aria-label="Back"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
            )}
            {title && (
              <h2 className="text-lg font-semibold text-text-primary">
                {sharingOpen && shareConfig ? 'Share' : title}
              </h2>
            )}
          </div>
          {dismissible && !hideCloseButton && (
            <button
              type="button"
              onClick={tryDismiss}
              className="min-w-touch min-h-touch flex items-center justify-center rounded-lg text-text-secondary hover:text-text-primary transition-colors -mr-2"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        <div className="flex-1 overflow-hidden relative min-h-0">
          <div
            className="absolute inset-0 overflow-y-auto px-4 py-4 transition-transform duration-300"
            style={{ transform: sharingOpen ? 'translateX(-100%)' : 'translateX(0)' }}
          >
            {children}
          </div>
          {shareConfig && (
            <div
              className="absolute inset-0 overflow-y-auto px-4 transition-transform duration-300"
              style={{ transform: sharingOpen ? 'translateX(0)' : 'translateX(100%)' }}
            >
              <ShareSheet
                mode="panel"
                entityType={shareConfig.entityType}
                entityId={shareConfig.entityId}
                entityTitle={shareConfig.entityTitle}
                onShareSuccess={shareConfig.onShareSuccess}
                defaultExpanded={true}
              />
            </div>
          )}
        </div>

        {(footer || shareConfig) && (
          <div
            className="px-4 border-t border-border-light flex items-center gap-2 flex-shrink-0"
            style={{ paddingTop: '12px', paddingBottom: 'calc(26px + env(safe-area-inset-bottom))' }}
          >
            {shareConfig && !sharingOpen && (
              <button
                type="button"
                onClick={() => setSharingOpen(true)}
                className="relative min-w-touch min-h-touch flex items-center justify-center rounded-lg text-text-secondary hover:text-text-primary transition-colors"
                aria-label="Share"
                title="Share"
              >
                <ShareIcon size={20} />
                {shareConfig.shareCount != null && shareConfig.shareCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-accent text-white text-[10px] font-bold flex items-center justify-center leading-none">
                    {shareConfig.shareCount}
                  </span>
                )}
              </button>
            )}
            {footer && !sharingOpen && (
              <div className="ml-auto flex items-center gap-2">
                {footer}
              </div>
            )}
          </div>
        )}
      </div>
      {showDebug && (
        <div style={{ position: 'fixed', top: 60, left: 8, background: 'rgba(0,0,0,0.88)', color: '#0f0', fontSize: 11, fontFamily: 'monospace', padding: '6px 8px', borderRadius: 4, zIndex: 99999, lineHeight: 1.7, whiteSpace: 'nowrap', pointerEvents: 'none' }}>
          <div>iOS: {debugVV.ios}</div>
          <div>innerH: {debugVV.innerH}</div>
          <div>vvH: {debugVV.vvH}</div>
          <div>vvOff: {debugVV.vvOff}</div>
          <div>belowVV: {debugVV.innerH - debugVV.vvOff - debugVV.vvH}px</div>
          <div>dvh: {debugVV.dvh}px</div>
          <div>lvh: {debugVV.lvh}px</div>
          <div>lvh-dvh: {debugVV.lvh - debugVV.dvh}px</div>
          <div>bodyPos: {debugVV.bodyPos}</div>
        </div>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// V2 Mobile — vaul-based, snap-point aware (spec 192)
// ════════════════════════════════════════════════════════════════════

const SNAP_FRACTIONS: Record<SheetSnap, number> = {
  peek: 0.4,
  half: 0.65,
  full: 0.95,
};

const DEFAULT_SNAPS: SheetSnap[] = ['peek', 'half', 'full'];

function snapToFraction(s: SheetSnap): number {
  return SNAP_FRACTIONS[s];
}

function fractionsFromSnaps(snaps: SheetSnap[]): number[] {
  return snaps.map(snapToFraction);
}

// Exported for unit tests — keeps the focus-snap predicate pure and
// independent of vaul / jsdom so we can pin the behavior without mounting
// the whole drawer tree.
export function isEditableFocusTarget(el: EventTarget | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false;
  if (el.tagName === 'INPUT') {
    // Skip non-text inputs that shouldn't trigger keyboard auto-snap
    // (checkbox, radio, button, submit, reset, file, color, range).
    const type = (el as HTMLInputElement).type.toLowerCase();
    const NON_TEXT = new Set([
      'button', 'submit', 'reset', 'checkbox', 'radio',
      'file', 'color', 'range', 'image', 'hidden',
    ]);
    return !NON_TEXT.has(type);
  }
  if (el.tagName === 'TEXTAREA') return true;
  if (el.isContentEditable) return true;
  // jsdom doesn't update isContentEditable unless the element is attached
  // to a document, so fall back to the property/attribute. Real browsers
  // set .contentEditable to 'true' | 'false' | 'plaintext-only' | 'inherit'.
  const ce = el.contentEditable;
  if (ce === 'true' || ce === 'plaintext-only') return true;
  return false;
}

function SheetMobileV2(props: SheetProps) {
  const {
    open,
    onDismiss,
    isOpen,
    onClose,
    title,
    footer,
    children,
    shareConfig,
    dismissible = true,
    hideCloseButton = false, ariaLabel,
    dirtyGuard = false,
    snapPoints = DEFAULT_SNAPS,
    initialSnap = 'full',
    autoSnapOnFocus = true,
  } = props;

  const resolvedOpen = open ?? isOpen ?? false;
  const noopDismiss = useMemo<() => void>(() => () => {}, []);
  const resolvedDismiss = onDismiss ?? onClose ?? noopDismiss;

  const fractions = useMemo(() => fractionsFromSnaps(snapPoints), [snapPoints]);
  const initialFraction = snapToFraction(initialSnap);
  const [activeSnap, setActiveSnap] = useState<number | string | null>(initialFraction);

  useEffect(() => {
    if (resolvedOpen) setActiveSnap(initialFraction);
  }, [resolvedOpen, initialFraction]);

  const [sharingOpen, setSharingOpen] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  useEffect(() => {
    if (!resolvedOpen) {
      setSharingOpen(false);
      setConfirmDiscard(false);
    }
  }, [resolvedOpen]);

  const tryDismiss = useCallback(() => {
    if (!dismissible) return;
    if (dirtyGuard) {
      setConfirmDiscard(true);
      return;
    }
    resolvedDismiss();
  }, [dismissible, dirtyGuard, resolvedDismiss]);

  // Android hardware/gesture back → dismiss (Q5: backButton does NOT bridge to Escape)
  useEffect(() => {
    if (!resolvedOpen || !dismissible) return;
    if (typeof window === 'undefined') return;
    const cap = (window as unknown as { Capacitor?: { isNativePlatform: () => boolean } }).Capacitor;
    if (!cap?.isNativePlatform?.()) return;

    let handle: { remove: () => void } | undefined;
    let cancelled = false;
    import('@capacitor/app').then(({ App }) => {
      if (cancelled) return;
      App.addListener('backButton', () => {
        tryDismiss();
      }).then((h) => {
        if (cancelled) h.remove();
        else handle = h;
      });
    });
    return () => {
      cancelled = true;
      handle?.remove();
    };
  }, [resolvedOpen, dismissible, tryDismiss]);

  // Backdrop opacity lerp: 0.3 (peek) → 0.6 (full). Opacity-only for GPU compositing.
  const backdropOpacity = useMemo(() => {
    const frac = typeof activeSnap === 'number' ? activeSnap : initialFraction;
    const min = SNAP_FRACTIONS.peek;
    const max = SNAP_FRACTIONS.full;
    const clamped = Math.max(min, Math.min(max, frac));
    const t = (clamped - min) / (max - min || 1);
    return 0.3 + t * 0.3;
  }, [activeSnap, initialFraction]);

  // prefers-reduced-motion freezes the backdrop at mid-opacity (0.45)
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  const effectiveBackdropOpacity = prefersReducedMotion.current ? 0.45 : backdropOpacity;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) return;
      tryDismiss();
    },
    [tryDismiss]
  );

  // Spec 192 AC: focusing a text input inside a sheet auto-snaps to 'full'.
  // vaul's `repositionInputs` only scrolls content — it does not elevate the
  // snap point — so we wire the snap transition ourselves. Guarded on the
  // caller's snapPoints set: if they didn't include 'full', we leave the
  // snap alone rather than fabricate a fraction they never opted into.
  const fullFraction = SNAP_FRACTIONS.full;
  const hasFullSnap = useMemo(() => fractions.includes(fullFraction), [fractions, fullFraction]);

  const handleFocusCapture = useCallback(
    (e: React.FocusEvent<HTMLDivElement>) => {
      if (!autoSnapOnFocus || !hasFullSnap) return;
      if (!isEditableFocusTarget(e.target)) return;
      setActiveSnap((current) => {
        if (typeof current === 'number' && current >= fullFraction) return current;
        return fullFraction;
      });
    },
    [autoSnapOnFocus, hasFullSnap, fullFraction]
  );

  const activeSnapDataAttr = typeof activeSnap === 'number' ? activeSnap.toString() : '';

  return (
    <Drawer.Root
      open={resolvedOpen}
      onOpenChange={handleOpenChange}
      snapPoints={fractions}
      activeSnapPoint={activeSnap}
      setActiveSnapPoint={setActiveSnap}
      dismissible={dismissible && !confirmDiscard}
      modal
      repositionInputs={autoSnapOnFocus}
    >
      <Drawer.Portal>
        <Drawer.Overlay
          className="fixed inset-0 z-[199] bg-black"
          style={{ opacity: effectiveBackdropOpacity }}
          data-testid="sheet-backdrop"
        />
        <Drawer.Content
          className={[
            'fixed inset-x-0 bottom-0 z-[200]',
            'bg-surface rounded-t-2xl shadow-2xl',
            'flex flex-col outline-none',
          ].join(' ')}
          style={{ height: '100%', maxHeight: '95vh' }}
          aria-label={ariaLabel || title || 'Sheet'}
          data-testid="sheet-v2"
          data-active-snap-fraction={activeSnapDataAttr}
          onFocusCapture={handleFocusCapture}
        >
          <Drawer.Title className="sr-only">{title || 'Sheet'}</Drawer.Title>

          {confirmDiscard && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-surface rounded-t-2xl px-6 gap-4">
              <p className="text-base font-semibold text-text-primary text-center">Discard changes?</p>
              <p className="text-sm text-text-secondary text-center">Your changes will be lost.</p>
              <div className="flex gap-3 w-full">
                <button
                  type="button"
                  className="flex-1 py-2.5 rounded-lg border border-border-light text-text-secondary hover:text-text-primary transition-colors text-sm font-medium"
                  onClick={() => setConfirmDiscard(false)}
                >
                  Keep editing
                </button>
                <button
                  type="button"
                  className="flex-1 py-2.5 rounded-lg bg-destructive text-white text-sm font-medium hover:opacity-90 transition-opacity"
                  onClick={() => { setConfirmDiscard(false); resolvedDismiss(); }}
                >
                  Discard
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-center pt-3 pb-1 touch-none" aria-hidden="true">
            <div className="w-9 h-1 rounded-full bg-border-light" data-testid="sheet-grabber" />
          </div>

          <div className="flex items-center justify-between px-4 pt-2 pb-0 flex-shrink-0">
            <div className="flex items-center gap-2">
              {sharingOpen && (
                <button
                  type="button"
                  onClick={() => setSharingOpen(false)}
                  className="min-w-touch min-h-touch flex items-center justify-center rounded-lg text-text-secondary hover:text-text-primary transition-colors -ml-2"
                  aria-label="Back"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
              )}
              {/* Render the heading when there is a title OR when sharing is
                  configured. Gating on `title` alone meant an untitled sheet
                  with shareConfig showed sighted users no indication it had
                  switched into the share panel — the state was conveyed only by
                  the icon button's aria-label, leaving assistive tech better
                  served than sighted users. Fixed 2026-09-07. */}
              {(title || shareConfig) && (
                <h2 className="text-lg font-semibold text-text-primary">
                  {sharingOpen && shareConfig ? 'Share' : title}
                </h2>
              )}
            </div>
            {dismissible && !hideCloseButton && (
              <button
                type="button"
                onClick={tryDismiss}
                className="min-w-touch min-h-touch flex items-center justify-center rounded-lg text-text-secondary hover:text-text-primary transition-colors -mr-2"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>

          <div className="flex-1 overflow-hidden relative min-h-0">
            <div
              className="absolute inset-0 overflow-y-auto px-4 py-4 transition-transform duration-300"
              style={{ transform: sharingOpen ? 'translateX(-100%)' : 'translateX(0)' }}
            >
              {children}
            </div>
            {shareConfig && (
              <div
                className="absolute inset-0 overflow-y-auto px-4 transition-transform duration-300"
                style={{ transform: sharingOpen ? 'translateX(0)' : 'translateX(100%)' }}
              >
                <ShareSheet
                  mode="panel"
                  entityType={shareConfig.entityType}
                  entityId={shareConfig.entityId}
                  entityTitle={shareConfig.entityTitle}
                  onShareSuccess={shareConfig.onShareSuccess}
                  defaultExpanded={true}
                />
              </div>
            )}
          </div>

          {(footer || shareConfig) && (
            <div
              className="px-4 border-t border-border-light flex items-center gap-2 flex-shrink-0"
              style={{ paddingTop: '12px', paddingBottom: 'calc(26px + env(safe-area-inset-bottom))' }}
            >
              {shareConfig && !sharingOpen && (
                <button
                  type="button"
                  onClick={() => setSharingOpen(true)}
                  className="relative min-w-touch min-h-touch flex items-center justify-center rounded-lg text-text-secondary hover:text-text-primary transition-colors"
                  aria-label="Share"
                  title="Share"
                >
                  <ShareIcon size={20} />
                  {shareConfig.shareCount != null && shareConfig.shareCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-accent text-white text-[10px] font-bold flex items-center justify-center leading-none">
                      {shareConfig.shareCount}
                    </span>
                  )}
                </button>
              )}
              {footer && !sharingOpen && (
                <div className="ml-auto flex items-center gap-2">
                  {footer}
                </div>
              )}
            </div>
          )}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
